import { animated, config, useTransition } from 'react-spring';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import './dropdown.scss';
import { usePopper } from 'react-popper';
import { Placement } from '@popperjs/core';
import classNames from 'classnames';
import { Portal } from '../portal/portal';

interface DropdownProps {
  trigger: (isOpen: boolean) => ReactNode;
  placement?: Placement;
  onOpen?: () => void;
  onClose?: () => void;
  closeOnClickInside?: boolean;
  showVector?: boolean;
  highlightBackground?: boolean;
}

export const Dropdown: React.FC<React.PropsWithChildren<DropdownProps>> = ({
  trigger = () => <></>,
  placement = 'bottom-end',
  showVector = false,
  highlightBackground = true,
  onClose = () => {},
  onOpen = () => {},
  closeOnClickInside = true,
  children,
}: React.PropsWithChildren<DropdownProps>) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [referenceElement, setReferenceElement] = useState<HTMLDivElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);

  const { styles, attributes } = usePopper(referenceElement, popperElement, { placement, strategy: 'fixed' });

  const transitions = useTransition(isOpen, {
    from: {
      opacity: 0,
      transform: `scale(${0.9})`,
      transformOrigin: 'top',
    },
    enter: { opacity: 1, display: 'flex', transform: `scale(${1})` },
    leave: { opacity: 0, display: 'none', transform: `scale(${0.9})` },
    config: config.gentle,
  });

  const closeDropdown = useCallback(() => {
    onClose();
    setIsOpen(false);
  }, [onClose]);

  const onCloseDropdown = useCallback(
    (e: Event) => {
      e.stopPropagation();
      closeDropdown();
      document.body.removeEventListener('close-dropdown', onCloseDropdown, true);
    },
    [closeDropdown],
  );

  const closeDropdownEvent = useCallback(
    (e: Event) => {
      const target = e.target as Node;
      if (!target || !referenceElement || !popperElement) return;

      const clickedInsideTrigger = referenceElement.contains(target);
      const clickedInsideDropdown = popperElement.contains(target);

      // If clicked inside trigger while dropdown is open, close it
      if (clickedInsideTrigger && isOpen) {
        document.body.dispatchEvent(new CustomEvent('close-dropdown'));
        return;
      }

      // If clicked inside dropdown and closeOnClickInside is false, do nothing
      if (clickedInsideDropdown && !closeOnClickInside) {
        return;
      }

      // If clicked outside both trigger and dropdown, or clicked inside dropdown with closeOnClickInside=true
      if (!clickedInsideTrigger) {
        document.body.dispatchEvent(new CustomEvent('close-dropdown'));
      }
    },
    [referenceElement, popperElement, isOpen, closeOnClickInside],
  );

  const handleTriggerClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!isOpen) {
      // If closed, open it
      setIsOpen(true);
    }
    // When open, closing is handled by the document click handler
  };

  useEffect(() => {
    if (!isOpen) return;

    onOpen();

    document.body.addEventListener('click', closeDropdownEvent, true);
    document.body.addEventListener('close-dropdown', onCloseDropdown, true);

    return () => {
      document.body.removeEventListener('click', closeDropdownEvent, true);
      document.body.removeEventListener('close-dropdown', onCloseDropdown, true);
    };
  }, [isOpen, closeDropdownEvent, onCloseDropdown]);

  return (
    <>
      <div className="flex w-full" ref={setReferenceElement} onClick={handleTriggerClick}>
        {trigger(isOpen)}

        <Portal>
          {transitions(
            (transitionStyle, item) =>
              item && (
                <div
                  ref={setPopperElement}
                  className="flex flex-col items-center justify-center gap-0"
                  style={{ zIndex: 2001, ...styles.popper }}
                  {...attributes.popper}
                >
                  {showVector && (
                    <animated.svg
                      style={transitionStyle}
                      width="30"
                      height="11"
                      viewBox="0 0 30 11"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M19.6723 2.72422L26.3503 9.47642C27.3145 10.4514 28.6288 11 30 11L-1.90735e-06 11C1.37123 11 2.68545 10.4514 3.64969 9.47642L10.3277 2.72422C12.8997 0.12359 17.1003 0.123592 19.6723 2.72422Z"
                        fill="white"
                      />
                    </animated.svg>
                  )}
                  <animated.div
                    className={classNames('quipli-v2 dropdown-content', { 'no-background': !highlightBackground })}
                    style={transitionStyle}
                  >
                    {children}
                  </animated.div>
                </div>
              ),
          )}
        </Portal>
      </div>
    </>
  );
};
